import React, { FC, useEffect, useReducer } from 'react';

import {
  Box,
  HStack,
  NumberInput,
  NumberInputField,
  NumberInputFieldProps,
  Stack,
} from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import map from 'lodash/map';
import round from 'lodash/round';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import {
  Card,
  ConditionLabel,
  FormActionErrorMessage,
} from 'components/DataDisplay';
import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import {
  defaultFormStyles,
  Form,
  FormAction,
  Select,
} from 'components/DataEntry';
import { LearnMoreButton } from 'components/DataEntry/Button/LearnMoreButton';
import { PolicyIcon } from 'components/Icons';
import { getIcon } from 'components/Icons/Components';
import { dataPolicyFormStyles } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/styles';
import { renderDisplayNameBox } from 'containers/Jit/MaskingPolicies/Components/DataPolicyWizard/utils';
import {
  jitApprovalPolicyConditionOptions,
  jitApprovalTypeOptions,
  jitProviderOptions,
} from 'containers/Jit/Setup/Components/Utils/utils';
import { selectJitSetup } from 'containers/Jit/Setup/selectors';
import { actions } from 'containers/Jit/Setup/slice';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

interface Props {
  action?: FormAction;
}

export const AddJitApprovalPolicy: FC<Props> = props => {
  const { action = 'Add' } = props;
  const dispatch = useDispatch();
  const { jitAccounts, jitAction, approvalPolicy } = useSelector(
    selectJitSetup,
  );
  const { accountMapping } = useCloudAccountId();
  const navigate = useNavigate();
  const { setLearnMore } = useKnowledgeBase();

  const [state, updateState] = useReducer(
    (prev, next) => {
      if (typeof next === 'function') {
        // functional update
        return next(prev);
      }
      // normal object merge
      return { ...prev, ...next };
    },
    {
      name: '',
      description: '',
      accountOptions: [],
      dataGroupOptions: [],
      condition: jitApprovalPolicyConditionOptions[0],
      conditionValues: [],
      duration: 2,
      action: jitApprovalTypeOptions[0],
      isEdit: false,
      isInvalid: false,
    },
  );

  useEffect(() => {
    if (action === 'Update') updateState({ isEdit: true });
  }, [action]);

  //get accounts
  useEffect(() => {
    dispatch(
      actions.getJitAccounts({
        q: { page: 1, size: 100 },
      }),
    );
  }, [dispatch, accountMapping]);

  useEffect(() => {
    dispatch(
      actions.getDataGroups({
        q: { page: 1, size: 100 },
        onSuccess: res => {
          updateState({
            dataGroupOptions: map(res?.items, o => ({
              label: o.name,
              value: o.uuid,
              data: o,
              icon: <PolicyIcon />,
            })),
          });
        },
      }),
    );
  }, [dispatch, state.account]);

  //set cloud account options
  useEffect(() => {
    const accounts = map(jitAccounts.data, o => ({
      label:
        accountMapping?.[o?.uuid]?.name +
        ' (' +
        accountMapping?.[o?.uuid]?.cloud_account_id +
        ')',
      value: o?.uuid,
      data: o,
      icon: getIcon(o?.cloud_type!),
    }));
    updateState({ accountOptions: accounts });
  }, [jitAccounts.data, accountMapping]);

  useEffect(() => {
    if (state.isEdit)
      updateState({
        name: approvalPolicy.data?.name,
        description: approvalPolicy.data?.description,
        duration: round(approvalPolicy.data?.max_duration_seconds / (60 * 60)),
      });
  }, [approvalPolicy.data, state.isEdit]);

  const onSubmit = type => {
    const payload = {
      name: state.name,
      description: state.description,
      filters: {
        [state.condition?.value]: map(state.conditionValues, o => o.value),
      },
      max_duration_seconds: state.duration * 60 * 60,
    };

    const onSuccess = () => {
      updateState({ isInvalid: false });
      navigate('/jit/setup/approval-policy');
    };
    const onError = () => updateState({ isInvalid: true });

    if (state.isEdit)
      dispatch(
        actions.updateApprovalPolicy({
          q: {
            autoApprovalPolicyUpdateRequest: payload,
            policyId: approvalPolicy.data?.id,
          },
          onSuccess,
          onError,
        }),
      );
    else
      dispatch(
        actions.createApprovalPolicy({
          q: {
            autoApprovalPolicyCreateRequest: payload,
          },
          onSuccess,
          onError,
        }),
      );
  };

  const renderCondition = field => {
    return (
      <HStack>
        {field === 'duration' || field === 'action' ? (
          renderDisplayNameBox(
            field === 'action' ? 'Approval' : 'Maximum duration (in hours)',
            '40%',
            customTheme.colors.gray['300'],
          )
        ) : (
          <Box w="40%" bg="white">
            <Select
              options={jitApprovalPolicyConditionOptions}
              value={state.condition}
              onChange={v => updateState({ condition: v, conditionValues: [] })}
              showClearIndicator={false}
            />
          </Box>
        )}
        <Box w="10%">
          {renderDisplayNameBox('is', 'full', customTheme.colors.gray['250'])}
        </Box>
        {field === 'duration' ? (
          <Box w="50%">
            <NumberInput
              value={state.duration}
              onChange={val => updateState({ duration: parseInt(val) })}
            >
              <NumberInputField
                {...(defaultFormStyles.textField
                  ?.input as NumberInputFieldProps)}
                bg="white"
              />
            </NumberInput>
          </Box>
        ) : (
          <Box w="50%" bg="white">
            <Select
              options={
                field === 'action'
                  ? jitApprovalTypeOptions
                  : state.condition?.value === 'data_group_ids'
                  ? state.dataGroupOptions
                  : state.condition?.value === 'account_ids'
                  ? state.accountOptions
                  : jitProviderOptions
              }
              value={
                field === 'duration'
                  ? state.durations
                  : field === 'action'
                  ? state.action
                  : state.conditionValues
              }
              onChange={v => {
                if (field === 'duration') updateState({ durations: v });
                else if (field === 'action') updateState({ action: v });
                else updateState({ conditionValues: v });
              }}
              showClearIndicator={field !== 'action'}
              isMulti={field !== 'action'}
              showTotalSelected
            />
          </Box>
        )}
      </HStack>
    );
  };

  return (
    <Card styles={{ cardContent: { pl: 32, ml: 2 } }}>
      <Form
        title={
          <HStack justify="space-between" w="full">
            <Box>{state.isEdit ? 'Update' : 'Add'} Approval Policy</Box>
            <LearnMoreButton
              onOpen={() => setLearnMore('jit_approvals')}
              label="JIT Approvals"
            />
          </HStack>
        }
        titleIcon={<PolicyIcon />}
        isLoading={approvalPolicy.isLoading}
        schema={{
          name: {
            type: 'text',
            label: 'Policy Name',
            value: state.name,
            onChange: val => {
              updateState({
                name: val,
                isInvalid: false,
              });
            },
            isDisabled: state.isEdit,
            placeholder: 'Enter policy name',
            isRequired: !state.isEdit,
          },
          description: {
            type: 'textArea',
            label: 'Description',
            placeholder:
              'This description is to codify what actions or policies need to ' +
              'be enforced when a certain condition is detected or found in the cloud environment',
            isRequired: !state.isEdit,
            onChange: val =>
              updateState({ description: val, isInvalid: false }),
            value: state.description,
          },
          conditions: {
            type: 'custom',
            isRequired: true,
            component: () => (
              <Stack spacing={3}>
                <ConditionLabel
                  name="IF"
                  customQuantifier={renderDisplayNameBox(
                    'all',
                    24,
                    customTheme.colors.gray['250'],
                  )}
                  label="of the following conditions are true"
                />
                <Stack
                  bg={customTheme.colors.gray['50']}
                  borderRadius={6}
                  spacing={5}
                  py={6}
                  px={6}
                >
                  {renderCondition('condition')}
                  {renderCondition('duration')}
                </Stack>
              </Stack>
            ),
          },
          action: {
            type: 'custom',
            isRequired: true,
            component: () => (
              <Stack spacing={3}>
                <ConditionLabel
                  name="THEN"
                  label="take the following actions"
                />
                <Stack
                  bg={customTheme.colors.gray['50']}
                  borderRadius={6}
                  spacing={5}
                  py={6}
                  px={6}
                >
                  {renderCondition('action')}
                </Stack>
              </Stack>
            ),
          },
          ...(state.isInvalid && !isEmpty(jitAction.error)
            ? {
                validatedMessage: {
                  type: 'custom',
                  component: () => (
                    <FormActionErrorMessage
                      description={jitAction.error?.description}
                    />
                  ),
                },
              }
            : {}),
        }}
        showHelp
        buttonOptions={{
          submit: {
            name: state.isEdit ? 'UPDATE' : 'OK',
            isLoading: jitAction.isLoading,
            isDisabled:
              ((!state.name || !state.description) && !state.isEdit) ||
              isEmpty(state.conditionValues),
          },
          reset: {
            name: 'CANCEL',
            onClick: () => navigate('/jit/setup/approval-policy'),
            isVisible: true,
          },
        }}
        styles={dataPolicyFormStyles}
        handleSubmit={() => onSubmit('submit')}
      />
      <LearnMore />
    </Card>
  );
};
